import clsx from 'clsx'
import { Observer } from 'mobx-react'
import React, { type RefObject, useEffect, useRef, useState } from 'react'

import type { StyleProps } from '~/@common/types'

import { DropdownContext } from './context'
import S from './Dropdown.style'

export type DropdownCustomButtonProps = {
  value: any
  placeholder?: string
  contents?: React.ReactNode
  toggleFn: (e: React.MouseEvent) => void
  isOpen: boolean
  disabled?: boolean
  className?: StyleProps['className']
  ref: RefObject<HTMLButtonElement>
}

export type DropdownRenderProps = {
  CustomButton: (props: DropdownCustomButtonProps) => React.ReactNode
  onChange?: (value: any) => void
  value?: any
  placeholder?: string
  width?: number
  children?: React.ReactNode
  disabled?: boolean
  buttonClassName?: StyleProps['className']
} & StyleProps

const _DropdownRender = ({
  buttonClassName,
  onChange,
  width,
  className,
  value,
  placeholder,
  children,
  CustomButton,
  style,
  disabled,
  ...props
}: DropdownRenderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedNode, setSelectedNode] = useState<React.ReactNode>(undefined)

  const dropDownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const open = () => {
    setIsOpen(true)

    requestAnimationFrame(() => {
      if (listRef.current) {
        const scrollEl = listRef.current.querySelector<HTMLDivElement>('.dropdown-scroll-wrap')
        const activeEl = listRef.current.querySelector<HTMLButtonElement>('.active')

        if (scrollEl && activeEl) {
          const scrollActiveItemCenter =
            activeEl.offsetTop - scrollEl.offsetHeight / 2 + activeEl.offsetHeight / 2

          scrollEl.scrollTop = scrollActiveItemCenter
        }
      }
    })
  }

  const close = () => {
    setIsOpen(false)
  }

  const toggle = () => {
    if (disabled) return
    isOpen ? close() : open()
  }

  const onDropdownSelect = (
    newValue: string | number,
    node: React.ReactNode,
    isOnChange = false,
  ) => {
    if (isOnChange && value !== newValue) {
      onChange?.(newValue)
    }
    if (node !== undefined) setSelectedNode(node)
  }

  const setScrollTop = (v: number) => {
    if (listRef.current) {
      // TODO: scroll 이동 넣기
      listRef.current.scrollTop = v
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return (
    <Observer>
      {() => (
        <DropdownContext.Provider
          value={{
            value,
            placeholder,
            onSelect: onDropdownSelect,
            isOpen,
            setScrollTop,
          }}
        >
          <div
            css={[S.basic.wrapper]}
            style={{ width: width ? `${width}px` : 'auto', ...style }}
            className={className}
            ref={dropDownRef}
            {...props}
          >
            {CustomButton({
              value,
              placeholder,
              contents: selectedNode,
              toggleFn: () => {
                toggle()
              },
              disabled,
              isOpen,
              className: buttonClassName,
              ref: buttonRef,
            })}
            <div className={clsx('__dropdown-list', isOpen && 'open')} ref={listRef}>
              {children}
            </div>
          </div>
        </DropdownContext.Provider>
      )}
    </Observer>
  )
}

_DropdownRender.displayName = 'DropdownRender'

const DropdownRender = _DropdownRender

export default DropdownRender
